import { useAppContext } from '@/hooks/useAppContext';
import { cn } from '@/lib/utils';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useLoginActions } from '@/hooks/useLoginActions';
import type { AppId } from '@/desktop/DesktopContext';
import { Moon, Sun, Monitor, LogOut } from 'lucide-react';

export function SettingsApp() {
  const { config, updateConfig } = useAppContext();
  const { user } = useCurrentUser();
  const { logout } = useLoginActions();

  const dockAppOptions: { id: AppId; label: string }[] = [
    { id: 'feed', label: 'Feed' },
    { id: 'search', label: 'Search' },
    { id: 'notifications', label: 'Notifications' },
    { id: 'articles', label: 'Articles' },
    { id: 'mail', label: 'Mail' },
    { id: 'dms', label: 'Messages' },
    { id: 'media', label: 'Media' },
    { id: 'nsite', label: 'Browser' },
    { id: 'relays', label: 'Relays' },
    { id: 'profile', label: 'Profile' },
    { id: 'settings', label: 'Settings' },
    { id: 'publish', label: 'Publish' },
  ];

  return (
    <div className="h-full overflow-auto">
      <div className="p-4 space-y-6">
        <div>
          <h2 className="text-sm font-semibold mb-3">Appearance</h2>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Theme</Label>
              <Select
                value={config.theme}
                onValueChange={(value: 'light' | 'dark' | 'system') =>
                  updateConfig((current) => ({ ...current, theme: value }))
                }
              >
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="light">
                    <div className="flex items-center gap-2">
                      <Sun className="w-4 h-4" />
                      Light
                    </div>
                  </SelectItem>
                  <SelectItem value="dark">
                    <div className="flex items-center gap-2">
                      <Moon className="w-4 h-4" />
                      Dark
                    </div>
                  </SelectItem>
                  <SelectItem value="system">
                    <div className="flex items-center gap-2">
                      <Monitor className="w-4 h-4" />
                      System
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <Separator />

        <div>
          <h2 className="text-sm font-semibold mb-3">Relays</h2>
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label className="text-xs">Use nostrVM App Relays</Label>
              <p className="text-[10px] text-muted-foreground">
                Override your NIP-65 relays with the curated nostrVM set (ditto, primal, damus)
              </p>
            </div>
            <Switch
              checked={config.useAppRelays}
              onCheckedChange={(checked) =>
                updateConfig((current) => ({
                  ...current,
                  useAppRelays: checked,
                }))
              }
            />
          </div>
        </div>

        <Separator />

        <div>
          <h2 className="text-sm font-semibold mb-3">Blossom</h2>
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label className="text-xs">Use App Blossom Servers</Label>
              <p className="text-[10px] text-muted-foreground">
                Merge app servers with your own
              </p>
            </div>
            <Switch
              checked={config.useAppBlossomServers}
              onCheckedChange={(checked) =>
                updateConfig((current) => ({
                  ...current,
                  useAppBlossomServers: checked,
                }))
              }
            />
          </div>
        </div>

        <Separator />

        <div>
          <h2 className="text-sm font-semibold mb-3">Dock</h2>
          <p className="text-[10px] text-muted-foreground mb-3">Choose which apps appear in the dock.</p>
          <div className="grid grid-cols-2 gap-2">
            {dockAppOptions.map((app) => {
              const isLocked = app.id === 'settings';
              return (
                <div key={app.id} className={cn('flex items-center gap-2', isLocked && 'opacity-70')}>
                  <Checkbox
                    id={`dock-${app.id}`}
                    checked={isLocked ? true : config.dockApps.includes(app.id)}
                    disabled={isLocked}
                    onCheckedChange={(checked) => {
                      const currentDock = config.dockApps || [];
                      const newDock = checked
                        ? [...currentDock, app.id]
                        : currentDock.filter((id) => id !== app.id);
                      updateConfig((current) => ({ ...current, dockApps: newDock }));
                    }}
                  />
                  <Label htmlFor={`dock-${app.id}`} className={cn('text-xs', !isLocked && 'cursor-pointer')}>
                    {app.label}
                  </Label>
                </div>
              );
            })}
            {/* Documents — disabled until file sync is stable */}
            <div className="flex items-center gap-2 opacity-50 cursor-not-allowed">
              <Checkbox id="dock-documents" disabled checked={false} />
              <Label htmlFor="dock-documents" className="text-xs cursor-not-allowed flex items-center gap-1.5">
                Documents
                <Badge variant="secondary" className="text-[9px] px-1 py-0 h-4 leading-none">
                  Coming Soon
                </Badge>
              </Label>
            </div>
          </div>
        </div>

        <Separator />

        <div>
          <h2 className="text-sm font-semibold mb-3">Publishing</h2>
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label className="text-xs">Attach &lsquo;via nostrVM&rsquo; attribution to my posts</Label>
              <p className="text-[10px] text-muted-foreground">
                Adds a link back to nostrVM on events you publish.
              </p>
            </div>
            <Switch
              checked={config.attachClientTag}
              onCheckedChange={(checked) =>
                updateConfig((current) => ({
                  ...current,
                  attachClientTag: checked,
                }))
              }
            />
          </div>
        </div>

        <Separator />

        <div>
          <h2 className="text-sm font-semibold mb-3">Account</h2>
          {user ? (
            <Button
              variant="destructive"
              size="sm"
              className="gap-2"
              onClick={() => logout()}
            >
              <LogOut className="w-4 h-4" />
              Logout
            </Button>
          ) : (
            <p className="text-xs text-muted-foreground">Not logged in</p>
          )}
        </div>

        <Separator />

        <div className="text-center">
          <p className="text-[10px] text-muted-foreground">
            Vibed with <a href="https://shakespeare.diy" target="_blank" rel="noopener noreferrer" className="underline hover:text-foreground">Shakespeare</a>
          </p>
        </div>
      </div>
    </div>
  );
}
